import React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Chip } from '@mui/material';
import LzMBase, { LzTBase } from '@lz/models/commons/Base';
import LzMMedia from '@lz/models/commons/Media';
import { lzFBytesToSize } from '@lz/services/Utils';
import LzXToolbar, { LzTToolbarItem } from '@lz/components/commons/Toolbar';

export interface LzPMediaInfo {
  forSelection?: boolean;
  record?: LzMBase | null;
  modelRef?: LzTBase;
  onActionIconClick: (key: string, rec: LzMBase) => void;
}

const LzXMediaInfo = (props: LzPMediaInfo) => {
  const modelRef = props.modelRef || LzMMedia;

  const getActionIcons = (): LzTToolbarItem[] => {
    const media = new modelRef(props.record);
    const out: LzTToolbarItem[] = [];

    if (props.onActionIconClick) {
      if (props.forSelection) {
        out.push({
          label: 'Select Media',
          icon: <CheckIcon />,
          color: 'success',
          variant: 'contained',
          onClick: () => props.onActionIconClick('select', media),
        });
      } else {
        if (media.deleted_at) {
          out.push({
            label: 'Restore',
            icon: <RestoreFromTrashIcon />,
            color: 'success',
            variant: 'contained',
            onClick: () => props.onActionIconClick('deleted_restore', media),
          });
          out.push({
            label: 'Delete Forever',
            icon: <DeleteForeverIcon />,
            color: 'error',
            variant: 'contained',
            onClick: () => props.onActionIconClick('deleted_purge', media),
          });
        } else {
          if (media.is_active) {
            out.push({
              label: 'De-Activate',
              icon: <CloseIcon />,
              color: 'warning',
              variant: 'contained',
              onClick: () => props.onActionIconClick('status', media),
            });
          } else {
            out.push({
              label: 'Activate',
              icon: <CheckIcon />,
              color: 'success',
              variant: 'contained',
              onClick: () => props.onActionIconClick('status', media),
            });
          }
          out.push({
            label: 'Delete',
            icon: <DeleteIcon />,
            color: 'error',
            variant: 'contained',
            onClick: () => props.onActionIconClick('delete', media),
          });
        }
      }
    }

    return out;
  };

  const media = new modelRef(props.record);

  return (
    <Paper className='lz-paper-box' variant='outlined'>
      <h2 style={{ marginTop: 0 }}>Media Info</h2>
      {props.record ? (
        <React.Fragment>
          <Typography
            variant='h5'
            color='secondary'
            style={{ textAlign: 'center' }}
          >
            {media.name}
          </Typography>

          {media.isImage ? (
            <img
              src={media.file_preview_path}
              alt={media.name}
              style={{
                display: 'block',
                margin: '1em auto',
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: 240,
              }}
            />
          ) : (
            <div
              style={{
                margin: '1em 0',
                height: 240,
              }}
            >
              {media.contentTypeIcon}
            </div>
          )}

          <Typography variant='subtitle1'>
            Filename: <b>{media.file_name}</b>
          </Typography>

          <Typography variant='subtitle1'>
            File Size: <b>{lzFBytesToSize(media.file_size)}</b>
          </Typography>

          <Typography variant='subtitle1'>
            Category:{' '}
            <b style={{ textTransform: 'capitalize' }}>{media.category}</b>
          </Typography>

          <Typography variant='subtitle1'>
            Visibility: <b>{media.is_private ? 'Private' : 'Public'}</b>
          </Typography>

          <Typography variant='subtitle1'>
            Status: &nbsp;
            <Chip
              size='small'
              className={
                'status-chip ' +
                (media.deleted_at
                  ? 'danger'
                  : media.is_active
                    ? 'success'
                    : 'warning')
              }
              label={
                media.deleted_at
                  ? 'deleted'
                  : media.is_active
                    ? 'active'
                    : 'in-active'
              }
            />
          </Typography>

          <LzXToolbar items={getActionIcons()} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Typography variant='overline' display='block' gutterBottom>
            no media selected...
          </Typography>
        </React.Fragment>
      )}
    </Paper>
  );
};

export default LzXMediaInfo;
